import {
  Autocomplete,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AccountState,
  AzureOnboardingStatusStatusEnum,
  Provider,
} from '@repo/api-gw-sdk';
import type { AzureAppRegistrationOption } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { CustomAccountDisplayName } from '@/components/form/customAccountDisplayName';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { Loader } from '@/components/layout/loading';
import { AzureTenantSelect } from '@/components/selectors/azureTenantSelect';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { StepErrorDialog } from '../../../../../components/dialogs/stepErrorDialog';
import { FormTextField } from '../../../../../components/form/formTextField';
import { BulletItem } from '../bulletItem';
import { DiscoveryConfigurationAccordion } from '../discoveryConfigurationAccordion';
import { extractErrorMessage } from '../errorHandling';
import { NumericBulletSection } from '../numericBulletSection';

export interface AzureSourceAccountState {
  tenantId: string;
  sourceConnectScope: 'managementGroup' | 'subscription';
  enableScopeSelection: boolean;
  sourceConnectMethod: 'terraform' | 'bash';
  sourceSubscriptionId: string;
  sourceManagementGroupId: string;
  eonInternalResourceGroupName?: string;
  onboardingFlow: boolean;
  name?: string;
}

const subscriptionIdConfig = getCloudFieldConfig(
  Provider.Azure,
  'subscriptionId'
);
const managementGroupIdConfig = getCloudFieldConfig(
  Provider.Azure,
  'managementGroupId'
);
const resourceGroupNameConfig = getCloudFieldConfig(
  Provider.Azure,
  'resourceGroupName'
);

const DEFAULT_SOURCE_INTERNAL_RG_NAME = 'eon-source-internal-rg';

export const ConnectAzureSourceAccount = <T extends AzureSourceAccountState>(
  props: StepProps<T>
) => {
  const {
    OnboardTenantAgainEnabled,
    AzureMultipleAppRegistrations,
    AzureCustomPermissionsModel,
  } = useFeatureFlags();
  const [isDeploying, setIsDeploying] = useState(false);
  const [customName, setCustomName] = useState('');
  const dal = useDAL();
  const { data: backupApps, isLoading } = dal.cloudAccounts.azureApps.useApps(
    'Source',
    props.wizardState.tenantId
  );
  const { data: appRegistrations } =
    dal.cloudAccounts.azureApps.useListAppRegistrations(
      AzureMultipleAppRegistrations ? props.wizardState.tenantId : undefined
    );
  const [selectedMgmtAppId, setSelectedMgmtAppId] = useState('');
  const [selectedDaAppId, setSelectedDaAppId] = useState('');
  const [error, setError] = useState<Error | null>(null);

  const managementApps = useMemo(
    () =>
      appRegistrations?.appRegistrations?.filter(
        (app: AzureAppRegistrationOption) =>
          app.applicationType === 'ManagementApp'
      ) || [],
    [appRegistrations]
  );
  const dataAccessApps = useMemo(
    () =>
      appRegistrations?.appRegistrations?.filter(
        (app: AzureAppRegistrationOption) =>
          app.applicationType === 'DataAccessApp'
      ) || [],
    [appRegistrations]
  );

  const effectiveMgmtAppId = AzureMultipleAppRegistrations
    ? (managementApps.find(
        (a: AzureAppRegistrationOption) => a.id === selectedMgmtAppId
      )?.applicationId ?? '')
    : backupApps?.managementAppId;
  const effectiveDaAppId = AzureMultipleAppRegistrations
    ? (dataAccessApps.find(
        (a: AzureAppRegistrationOption) => a.id === selectedDaAppId
      )?.applicationId ?? '')
    : backupApps?.backupAppId;

  const sourceSubscriptionId =
    props.wizardState.sourceConnectScope === 'subscription'
      ? (props.wizardState.sourceSubscriptionId ?? '')
      : '';

  const sourceManagementGroupId =
    props.wizardState.sourceConnectScope === 'managementGroup'
      ? (props.wizardState.sourceManagementGroupId ?? '')
      : '';

  const terraformCode = `\
module "azure_source" {
  source = "https://eon.blob.core.windows.net/public/onboarding/latest/source/terraform/source.zip"
  management_app_id      = "${effectiveMgmtAppId ?? ''}"
  backup_app_id          = "${effectiveDaAppId ?? ''}"
  ${props.wizardState.sourceConnectScope === 'subscription' ? `subscription_id        = "${sourceSubscriptionId}"` : `management_group_id    = "${sourceManagementGroupId}"`}
}

output "source_onboarding" {
  value = module.azure_source
}`;

  const bashCode = `\
# Log in to the tenant
az login --tenant "${props.wizardState.tenantId}"
${
  sourceSubscriptionId
    ? `\n# Set the subscription ID
az account set --subscription "${sourceSubscriptionId}"\n`
    : ''
}
# Get access tokens from Azure
ARM_TOKEN=$(az account get-access-token --query accessToken -o tsv)
GRAPH_TOKEN=$(az account get-access-token --resource https://graph.microsoft.com/ --query accessToken -o tsv)

# Run the connection script
curl -fsSL https://eon.blob.core.windows.net/public/onboarding/latest/source/bash/source.sh -o source.sh && chmod +x source.sh && \\
AZURE_ACCESS_TOKEN="$ARM_TOKEN" \\
AZURE_GRAPH_TOKEN="$GRAPH_TOKEN" \\
./source.sh \\
  --subscription-id "${sourceSubscriptionId}" \\
  --management-group-id "${sourceManagementGroupId}" \\
  --management-app-id "${effectiveMgmtAppId ?? ''}" \\
  --backup-app-id "${effectiveDaAppId ?? ''}"
`;

  const code =
    props.wizardState.sourceConnectMethod === 'terraform'
      ? terraformCode
      : bashCode;

  const isMissingFields =
    !props.wizardState.tenantId ||
    (props.wizardState.sourceConnectScope === 'subscription'
      ? !props.wizardState.sourceSubscriptionId
      : !props.wizardState.sourceManagementGroupId);

  const canGoNext = () => {
    const validationErrors =
      props.wizardState.sourceConnectScope === 'subscription'
        ? !subscriptionIdConfig.validation.validateInput(
            props.wizardState.sourceSubscriptionId
          ) ||
          !props.wizardState.eonInternalResourceGroupName ||
          !resourceGroupNameConfig.validation.validateInput(
            props.wizardState.eonInternalResourceGroupName
          )
        : !managementGroupIdConfig.validation.validateInput(
            props.wizardState.sourceManagementGroupId
          );

    const missingAppSelection =
      AzureMultipleAppRegistrations &&
      props.wizardState.sourceConnectScope === 'subscription' &&
      (!selectedMgmtAppId || !selectedDaAppId);

    return (
      !isDeploying &&
      !validationErrors &&
      !isMissingFields &&
      !missingAppSelection
    );
  };

  const connectManagementGroup = async () => {
    const response = await dal.cloudAccounts.managementGroups.connect({
      tenantId: props.wizardState.tenantId,
      sourceManagementGroupId: sourceManagementGroupId,
    });
    if (response.status !== AzureOnboardingStatusStatusEnum.Succeeded) {
      throw new Error('Failed to deploy management group');
    }
  };

  const connectSubscription = async () => {
    const { sourceAccount } = await dal.cloudAccounts.source.connect.azure({
      tenantId: props.wizardState.tenantId,
      sourceSubscriptionId: sourceSubscriptionId,
      sourceManagementGroupId: sourceManagementGroupId,
      eonInternalResourceGroupName:
        props.wizardState.eonInternalResourceGroupName,
      name: customName || undefined,
      managementAppRegistrationId:
        AzureMultipleAppRegistrations && selectedMgmtAppId
          ? selectedMgmtAppId
          : undefined,
      dataAccessAppRegistrationId:
        AzureMultipleAppRegistrations && selectedDaAppId
          ? selectedDaAppId
          : undefined,
    });
    if (
      sourceAccount &&
      sourceAccount.status !== AccountState.ACCOUNT_STATE_CONNECTED
    ) {
      throw new Error('Failed to deploy source account');
    }
  };

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);
    try {
      if (props.wizardState.sourceConnectScope === 'managementGroup') {
        await connectManagementGroup();
      } else {
        await connectSubscription();
      }
      await props.finish();
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const bulletTitle = AzureCustomPermissionsModel
    ? `Select the ${props.wizardState.sourceConnectScope === 'subscription' ? 'subscription' : 'management group'} you want to connect and the app registrations to use.`
    : `Tell us ${props.wizardState.onboardingFlow ? 'what source' : props.wizardState.sourceConnectScope === 'subscription' ? 'which subscription' : 'which management group'} you want to connect, and then
                  ${
                    props.wizardState.sourceConnectMethod === 'terraform'
                      ? 'apply the generated Terraform module'
                      : 'run the generated Bash command'
                  } in your environment. ${props.wizardState.onboardingFlow ? ' If you connect a management group, all subscriptions under it will be connected.' : props.wizardState.sourceConnectScope === 'subscription' ? '' : ' All subscriptions under it will be connected.'}`;

  return (
    <>
      <StepContainer
        stepperIndex={3}
        hideStepper={
          !props.wizardState.onboardingFlow || !OnboardTenantAgainEnabled
        }
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={
          isDeploying
            ? 'Please wait for the deployment to complete'
            : AzureMultipleAppRegistrations &&
                props.wizardState.sourceConnectScope === 'subscription' &&
                (!selectedMgmtAppId || !selectedDaAppId)
              ? 'Please select both a Management App and a Data Access App'
              : props.wizardState.sourceConnectScope === 'subscription'
                ? 'Please fill valid subscription ID'
                : 'Please fill valid management group ID'
        }
        nextButtonText={'Connect'}
        backButtonText={
          !props.wizardState.onboardingFlow || !OnboardTenantAgainEnabled
            ? 'Cancel'
            : 'Back'
        }
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack gap='min(60px, 4vw)'>
          <Stack divider={<Divider />} gap='24px'>
            {AzureCustomPermissionsModel ? (
              <Stack sx={{ paddingX: 'min(100px,6vw)', paddingTop: '16px' }}>
                <Typography variant='body2'>
                  Select the subscription you want to connect and choose the app
                  registrations to use. Make sure the selected apps have the
                  required roles assigned on this subscription and its internal
                  resource group before connecting.
                </Typography>
              </Stack>
            ) : (
              <TerraformBashToggle
                text={
                  props.wizardState.onboardingFlow
                    ? `Now we'll connect a source subscription or management group, and set the resources you want Eon to discover.
                  Eon discovers all resources by default, but you can override this behavior with discovery configuration below.`
                    : 'Before you start, make sure you can manage service principals in your Azure environment.'
                }
                connectMethod={props.wizardState.sourceConnectMethod}
                setConnectMethod={(connectMethod) => {
                  props.setWizardState({
                    ...props.wizardState,
                    sourceConnectMethod: connectMethod,
                  });
                }}
              />
            )}
            <Stack
              paddingX='min(100px,6vw)'
              paddingTop='16px'
              width='100%'
              height='auto'
            >
              {isLoading ? (
                <Loader />
              ) : (
                <Stack
                  gap='40px'
                  divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                >
                  <NumericBulletSection
                    bulletNumber={1}
                    title={
                      AzureCustomPermissionsModel
                        ? 'Connect source subscription'
                        : "Deploy Eon's source subscription"
                    }
                  >
                    <BulletItem
                      title={bulletTitle}
                      leftContent={
                        <Stack
                          gap='24px'
                          divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                        >
                          <Stack gap='24px'>
                            {!props.wizardState.onboardingFlow && (
                              <AzureTenantSelect
                                tenantId={props.wizardState.tenantId}
                                onChange={(tenantId) => {
                                  setSelectedMgmtAppId('');
                                  setSelectedDaAppId('');
                                  props.setWizardState({
                                    ...props.wizardState,
                                    tenantId,
                                  });
                                }}
                              />
                            )}
                            {props.wizardState.enableScopeSelection && (
                              <FormControl>
                                <FormLabel>
                                  <Typography variant='subtitle1'>
                                    Connection scope
                                  </Typography>
                                </FormLabel>
                                <ToggleButtonGroup
                                  sx={{ width: 'max-content' }}
                                  exclusive
                                  value={props.wizardState.sourceConnectScope}
                                  onChange={(_, value) => {
                                    if (value === null) {
                                      return;
                                    }
                                    props.setWizardState({
                                      ...props.wizardState,
                                      sourceConnectScope: value,
                                    });
                                  }}
                                >
                                  <ToggleButton value='subscription'>
                                    Subscription
                                  </ToggleButton>
                                  <ToggleButton value='managementGroup'>
                                    Management group
                                  </ToggleButton>
                                </ToggleButtonGroup>
                              </FormControl>
                            )}
                            <FormTextField
                              title='Subscription ID'
                              sx={{
                                display:
                                  props.wizardState.sourceConnectScope ===
                                  'subscription'
                                    ? 'inline-flex'
                                    : 'none',
                              }}
                              placeholder={subscriptionIdConfig.placeholder}
                              validation={subscriptionIdConfig.validation}
                              value={
                                props.wizardState.sourceSubscriptionId || ''
                              }
                              onChange={(event) =>
                                props.setWizardState({
                                  ...props.wizardState,
                                  sourceSubscriptionId: event.target.value,
                                })
                              }
                            />
                            <FormTextField
                              title='Management group ID'
                              sx={{
                                display:
                                  props.wizardState.sourceConnectScope ===
                                  'managementGroup'
                                    ? 'inline-flex'
                                    : 'none',
                              }}
                              placeholder={managementGroupIdConfig.placeholder}
                              validation={managementGroupIdConfig.validation}
                              value={
                                props.wizardState.sourceManagementGroupId || ''
                              }
                              onChange={(event) =>
                                props.setWizardState({
                                  ...props.wizardState,
                                  sourceManagementGroupId: event.target.value,
                                })
                              }
                            />
                          </Stack>
                          {AzureMultipleAppRegistrations &&
                            props.wizardState.sourceConnectScope ===
                              'subscription' &&
                            managementApps.length > 0 && (
                              <Stack gap='16px'>
                                <Typography variant='subtitle1'>
                                  App Registrations
                                </Typography>
                                <FormControl size='small'>
                                  <FormLabel>Management App</FormLabel>
                                  <Autocomplete
                                    size='small'
                                    options={managementApps}
                                    value={
                                      managementApps.find(
                                        (a: AzureAppRegistrationOption) =>
                                          a.id === selectedMgmtAppId
                                      ) || null
                                    }
                                    onChange={(_, val) =>
                                      setSelectedMgmtAppId(val?.id || '')
                                    }
                                    getOptionLabel={(
                                      opt: AzureAppRegistrationOption
                                    ) =>
                                      opt.displayName
                                        ? `${opt.displayName} (${opt.applicationId})`
                                        : opt.applicationId
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder='Select management app'
                                      />
                                    )}
                                  />
                                </FormControl>
                                <FormControl size='small'>
                                  <FormLabel>Data Access App</FormLabel>
                                  <Autocomplete
                                    size='small'
                                    options={dataAccessApps}
                                    value={
                                      dataAccessApps.find(
                                        (a: AzureAppRegistrationOption) =>
                                          a.id === selectedDaAppId
                                      ) || null
                                    }
                                    onChange={(_, val) =>
                                      setSelectedDaAppId(val?.id || '')
                                    }
                                    getOptionLabel={(
                                      opt: AzureAppRegistrationOption
                                    ) =>
                                      opt.displayName
                                        ? `${opt.displayName} (${opt.applicationId})`
                                        : opt.applicationId
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder='Select data access app'
                                      />
                                    )}
                                  />
                                </FormControl>
                              </Stack>
                            )}
                          {AzureCustomPermissionsModel &&
                            props.wizardState.sourceConnectScope ===
                              'subscription' && (
                              <FormTextField
                                title='Internal Resource Group Name'
                                placeholder='e.g. eon-source-internal-rg'
                                validation={resourceGroupNameConfig.validation}
                                value={
                                  props.wizardState
                                    .eonInternalResourceGroupName || ''
                                }
                                onChange={(event) =>
                                  props.setWizardState({
                                    ...props.wizardState,
                                    eonInternalResourceGroupName:
                                      event.target.value,
                                  })
                                }
                              />
                            )}
                          {props.wizardState.sourceConnectScope ===
                            'subscription' && (
                            <CustomAccountDisplayName
                              entityName='subscription'
                              customName={customName}
                              setCustomName={setCustomName}
                            />
                          )}
                        </Stack>
                      }
                      rightContent={
                        AzureCustomPermissionsModel ? (
                          <Stack
                            gap='16px'
                            sx={{
                              padding: '24px',
                              backgroundColor:
                                'var(--mui-palette-warning-bgLight)',
                              borderRadius: '8px',
                              borderLeft:
                                '4px solid var(--mui-palette-warning-main)',
                            }}
                          >
                            <Typography
                              variant='subtitle1'
                              sx={{ fontWeight: 500 }}
                            >
                              Pre-connection checklist
                            </Typography>
                            <Typography
                              variant='body2'
                              sx={{
                                color: 'var(--mui-palette-text-secondary)',
                              }}
                            >
                              Before connecting, please verify:
                            </Typography>
                            <Stack
                              component='ul'
                              gap='12px'
                              sx={{ margin: 0, paddingLeft: '20px' }}
                            >
                              <Typography component='li' variant='body2'>
                                The selected management and data access apps
                                exist in your tenant
                              </Typography>
                              <Typography component='li' variant='body2'>
                                The apps have the required roles assigned on
                                this subscription
                              </Typography>
                              <Typography component='li' variant='body2'>
                                The apps have the required roles assigned on the
                                resource group you allocated for Eon in your
                                subscription
                              </Typography>
                            </Stack>
                          </Stack>
                        ) : (
                          <CopyableCode
                            title={
                              props.wizardState.sourceConnectMethod ===
                              'terraform'
                                ? 'main.tf'
                                : 'source.sh'
                            }
                            code={code}
                            blur={isMissingFields}
                          />
                        )
                      }
                    />
                  </NumericBulletSection>
                  {props.wizardState.sourceConnectScope === 'subscription' &&
                    !AzureCustomPermissionsModel && (
                      <NumericBulletSection
                        bulletNumber={2}
                        title='Complete configuration'
                      >
                        <BulletItem
                          title={`After running the ${
                            props.wizardState.sourceConnectMethod ===
                            'terraform'
                              ? 'Terraform module'
                              : 'Bash command'
                          }, paste the output value below:`}
                          leftContent={
                            <Stack gap='8px' alignItems='flex-start'>
                              <FormTextField
                                title='eon_internal_resource_group_name'
                                placeholder={DEFAULT_SOURCE_INTERNAL_RG_NAME}
                                validation={resourceGroupNameConfig.validation}
                                value={
                                  props.wizardState
                                    .eonInternalResourceGroupName || ''
                                }
                                onChange={(event) =>
                                  props.setWizardState({
                                    ...props.wizardState,
                                    eonInternalResourceGroupName:
                                      event.target.value,
                                  })
                                }
                              />
                              <Button
                                size='small'
                                variant='text'
                                onClick={() =>
                                  props.setWizardState({
                                    ...props.wizardState,
                                    eonInternalResourceGroupName:
                                      DEFAULT_SOURCE_INTERNAL_RG_NAME,
                                  })
                                }
                              >
                                Use default name
                              </Button>
                            </Stack>
                          }
                        />
                      </NumericBulletSection>
                    )}
                </Stack>
              )}
            </Stack>
          </Stack>
          {props.wizardState.onboardingFlow && (
            <Stack gap='24px'>
              <Divider />
              <DiscoveryConfigurationAccordion />
              <Divider />
            </Stack>
          )}
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
